import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { apiClient } from '@kss/api';
import { ArrowLeft, Plus, Save, Send, Trash2 } from 'lucide-react';
import { EntityPicker } from '../sales/EntityPicker';

/**
 * Journal Voucher composer.
 *
 * Double-entry editor with a hard "debits must equal credits" gate
 * before posting. Mirrors the line-table UX of the sales/purchase
 * composers but each row picks a chart-of-accounts node and contributes
 * either a debit or a credit (mutually exclusive).
 */

type VoucherType = 'Journal' | 'Payment' | 'Receipt' | 'Contra' | 'Sales' | 'Purchase';

interface ChartOfAccount extends Record<string, unknown> {
  chartOfAccountId?: string;
  id?: number | string;
  code?: string;
  name?: string;
  accountType?: string | number;
}

interface Line {
  key: string;
  accountId: string | number | null;
  accountLabel: string;
  narration: string;
  debit: number;
  credit: number;
}

const VOUCHER_TYPES: VoucherType[] = ['Journal', 'Payment', 'Receipt', 'Contra', 'Sales', 'Purchase'];

const inputCls =
  'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-9 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const textareaCls =
  'min-h-[60px] w-full rounded-md border border-border bg-surface-card px-3 py-2 text-sm text-content-primary outline-none focus:border-primary';

export default function JournalVoucherComposer() {
  const { id: rawId } = useParams<{ id?: string }>();
  const isEdit = !!rawId && rawId !== 'new';
  const navigate = useNavigate();
  const toast = useToast();

  const [voucherNumber, setVoucherNumber] = useState('');
  const [voucherDate, setVoucherDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [voucherType, setVoucherType] = useState<VoucherType>('Journal');
  const [reference, setReference] = useState('');
  const [narration, setNarration] = useState('');

  const [lines, setLines] = useState<Line[]>([
    { key: rid(), accountId: null, accountLabel: '', narration: '', debit: 0, credit: 0 },
    { key: rid(), accountId: null, accountLabel: '', narration: '', debit: 0, credit: 0 },
  ]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<'draft' | 'post' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Hydrate when editing
  useEffect(() => {
    if (!isEdit || !rawId) return;
    setLoading(true);
    apiClient
      .get(`/accounting/JournalEntry/${encodeURIComponent(rawId)}`)
      .then((r) => {
        const d = r.data?.data ?? r.data;
        if (!d || typeof d !== 'object') return;
        setVoucherNumber(String(d.voucherNumber ?? d.code ?? ''));
        setVoucherDate(String(d.voucherDate ?? d.entryDate ?? '').slice(0, 10) || voucherDate);
        setVoucherType((d.voucherType ?? 'Journal') as VoucherType);
        setReference(String(d.reference ?? ''));
        setNarration(String(d.narration ?? d.description ?? ''));
        const ls = Array.isArray(d.lines) ? d.lines : [];
        if (ls.length) {
          setLines(
            ls.map((l: any) => ({
              key: rid(),
              accountId: l.accountId ?? l.chartOfAccountId ?? null,
              accountLabel: l.accountName ?? l.description ?? '',
              narration: l.narration ?? l.description ?? '',
              debit: Number(l.debit ?? 0),
              credit: Number(l.credit ?? 0),
            })),
          );
        }
      })
      .catch((e) => setErr(e instanceof Error ? e.message : 'Load failed'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawId]);

  // Totals + balance check
  const totals = useMemo(() => {
    let totalDebit = 0;
    let totalCredit = 0;
    for (const l of lines) {
      totalDebit += Number(l.debit) || 0;
      totalCredit += Number(l.credit) || 0;
    }
    const diff = round2(totalDebit - totalCredit);
    return { totalDebit, totalCredit, diff, balanced: Math.abs(diff) < 0.01 };
  }, [lines]);

  function updateLine(idx: number, patch: Partial<Line>) {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((ls) => [...ls, { key: rid(), accountId: null, accountLabel: '', narration: '', debit: 0, credit: 0 }]);
  }
  function removeLine(idx: number) {
    setLines((ls) => (ls.length <= 2 ? ls : ls.filter((_, i) => i !== idx)));
  }

  function pickAccount(idx: number, a: ChartOfAccount | null) {
    if (!a) {
      updateLine(idx, { accountId: null, accountLabel: '' });
      return;
    }
    updateLine(idx, {
      accountId: a.chartOfAccountId ?? a.id ?? null,
      accountLabel: `${a.code ? `${a.code} · ` : ''}${a.name ?? ''}`,
    });
  }

  // Mutually exclusive debit/credit on the same line.
  function setDebit(idx: number, v: number) {
    updateLine(idx, { debit: Math.max(0, v), credit: 0 });
  }
  function setCredit(idx: number, v: number) {
    updateLine(idx, { credit: Math.max(0, v), debit: 0 });
  }

  // Auto-fill the missing side from the running difference when the user
  // starts a new line (helps single-handed JV entry).
  function fillBalance(idx: number, side: 'debit' | 'credit') {
    const others = lines.reduce(
      (acc, l, i) => {
        if (i === idx) return acc;
        return { d: acc.d + (Number(l.debit) || 0), c: acc.c + (Number(l.credit) || 0) };
      },
      { d: 0, c: 0 },
    );
    if (side === 'debit') {
      const need = Math.max(0, others.c - others.d);
      if (need > 0) updateLine(idx, { debit: round2(need), credit: 0 });
    } else {
      const need = Math.max(0, others.d - others.c);
      if (need > 0) updateLine(idx, { credit: round2(need), debit: 0 });
    }
  }

  function validate(): string | null {
    if (lines.length < 2) return 'A journal voucher needs at least two lines.';
    for (const [i, l] of lines.entries()) {
      if (!l.accountId) return `Line ${i + 1}: pick a ledger account.`;
      if ((Number(l.debit) || 0) === 0 && (Number(l.credit) || 0) === 0)
        return `Line ${i + 1}: enter a debit or credit amount.`;
      if ((Number(l.debit) || 0) > 0 && (Number(l.credit) || 0) > 0)
        return `Line ${i + 1}: a single line can't have both a debit and a credit.`;
    }
    if (!totals.balanced)
      return `Debits (${inr(totals.totalDebit)}) and credits (${inr(totals.totalCredit)}) don't balance — difference ₹${inr(Math.abs(totals.diff))}.`;
    return null;
  }

  async function save(status: 'Draft' | 'Posted') {
    if (status === 'Posted') {
      const e = validate();
      if (e) {
        setErr(e);
        toast.error('Cannot post', e);
        return;
      }
    }
    setSaving(status === 'Draft' ? 'draft' : 'post');
    setErr(null);
    try {
      const payload = {
        voucherNumber: voucherNumber || undefined,
        voucherDate,
        voucherType,
        reference: reference || undefined,
        narration: narration || undefined,
        voucherStatus: status,  // §35.2: workflow status (Draft|Posted)
        totalDebit: round2(totals.totalDebit),
        totalCredit: round2(totals.totalCredit),
        status: 0,  // §35.2: row-level status (0=Active)
        lines: lines.map((l, i) => ({
          lineNumber: i + 1,
          accountId: l.accountId,
          accountName: l.accountLabel,
          narration: l.narration || undefined,
          debit: round2(Number(l.debit) || 0),
          credit: round2(Number(l.credit) || 0),
        })),
      };

      if (isEdit && rawId) {
        // §35.3 / G53: PK is `code` (string), not numeric `id`.
        await apiClient.put('/accounting/JournalEntry', { ...payload, code: rawId });
        toast.success('Saved', `JV ${status === 'Posted' ? 'posted' : 'updated'}`);
      } else {
        await apiClient.post('/accounting/JournalEntry', payload);
        toast.success('Saved', `JV ${status === 'Posted' ? 'posted' : 'saved as draft'}`);
      }
      navigate('/panel/accounting/journal');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setErr(msg);
      toast.error('Save failed', msg);
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <Button variant="outline" size="sm" onClick={() => navigate('/panel/accounting/journal')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">
            {isEdit ? 'Edit journal voucher' : 'New journal voucher'}
          </h1>
          <p className="text-xs text-content-muted">
            {voucherType} ·{' '}
            <span className={totals.balanced ? 'text-success' : 'text-warning'}>
              {totals.balanced
                ? `Balanced ₹${inr(totals.totalDebit)}`
                : `Out by ₹${inr(Math.abs(totals.diff))}`}
            </span>
          </p>
        </div>
        <Button variant="outline" disabled={!!saving || loading} onClick={() => save('Draft')}>
          <Save className="h-4 w-4" /> {saving === 'draft' ? 'Saving…' : 'Save draft'}
        </Button>
        <Button disabled={!!saving || loading || !totals.balanced} onClick={() => save('Posted')}>
          <Send className="h-4 w-4" /> {saving === 'post' ? 'Posting…' : 'Save & post'}
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <Card>
        <div className="grid gap-3 md:grid-cols-4">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Voucher number</span>
            <input
              className={inputCls}
              value={voucherNumber}
              onChange={(e) => setVoucherNumber(e.target.value)}
              placeholder="auto-generated if blank"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Voucher date</span>
            <input
              type="date"
              className={inputCls}
              value={voucherDate}
              onChange={(e) => setVoucherDate(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Voucher type</span>
            <select
              className={selectCls}
              value={voucherType}
              onChange={(e) => setVoucherType(e.target.value as VoucherType)}
            >
              {VOUCHER_TYPES.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Reference</span>
            <input
              className={inputCls}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Bank ref / cheque # / contra ID"
            />
          </label>
          <label className="flex flex-col gap-1 md:col-span-4">
            <span className="text-xs font-medium text-content-secondary">Narration</span>
            <textarea
              className={textareaCls}
              value={narration}
              onChange={(e) => setNarration(e.target.value)}
              placeholder="Being… (visible on the printed voucher and the audit log)"
            />
          </label>
        </div>
      </Card>

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-content-primary">Entries</h3>
          <Button size="sm" variant="outline" onClick={addLine}>
            <Plus className="h-4 w-4" /> Add line
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-content-muted">
              <tr className="border-b border-border">
                <th className="px-2 py-2 text-left">#</th>
                <th className="px-2 py-2 text-left">Ledger account</th>
                <th className="px-2 py-2 text-left">Narration</th>
                <th className="px-2 py-2 text-right">Debit (₹)</th>
                <th className="px-2 py-2 text-right">Credit (₹)</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.key} className="border-b border-border/40 align-top">
                  <td className="px-2 py-2 text-content-muted">{i + 1}</td>
                  <td className="px-2 py-2" style={{ minWidth: 240 }}>
                    <EntityPicker<ChartOfAccount>
                      basePath="/accounting/ChartOfAccount"
                      idKey="chartOfAccountId"
                      labelKey="name"
                      secondaryKey="code"
                      value={l.accountId}
                      onChange={(a) => pickAccount(i, a)}
                      placeholder="Search account by name or code"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      className={inputCls}
                      value={l.narration}
                      onChange={(e) => updateLine(i, { narration: e.target.value })}
                      placeholder="(optional)"
                    />
                  </td>
                  <td className="px-2 py-2 text-right">
                    <div className="flex flex-col items-end gap-1">
                      <input
                        type="number"
                        className={`${inputCls} text-right`}
                        value={l.debit || ''}
                        min={0}
                        step={0.01}
                        onChange={(e) => setDebit(i, Number(e.target.value))}
                        style={{ width: 130 }}
                      />
                      {!l.debit && !l.credit && (
                        <button
                          type="button"
                          className="text-[10px] text-primary hover:underline"
                          onClick={() => fillBalance(i, 'debit')}
                        >
                          balance
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right">
                    <div className="flex flex-col items-end gap-1">
                      <input
                        type="number"
                        className={`${inputCls} text-right`}
                        value={l.credit || ''}
                        min={0}
                        step={0.01}
                        onChange={(e) => setCredit(i, Number(e.target.value))}
                        style={{ width: 130 }}
                      />
                      {!l.debit && !l.credit && (
                        <button
                          type="button"
                          className="text-[10px] text-primary hover:underline"
                          onClick={() => fillBalance(i, 'credit')}
                        >
                          balance
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <button
                      type="button"
                      className="text-content-muted hover:text-danger"
                      onClick={() => removeLine(i)}
                      aria-label="Remove line"
                      disabled={lines.length <= 2}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-border">
                <td colSpan={3} className="px-2 py-2 text-right text-xs font-medium text-content-muted">
                  Totals
                </td>
                <td className="px-2 py-2 text-right font-semibold tabular-nums">
                  ₹ {inr(totals.totalDebit)}
                </td>
                <td className="px-2 py-2 text-right font-semibold tabular-nums">
                  ₹ {inr(totals.totalCredit)}
                </td>
                <td />
              </tr>
              <tr>
                <td colSpan={3} className="px-2 py-2 text-right text-xs text-content-muted">
                  Difference (must be zero to post)
                </td>
                <td colSpan={2} className={`px-2 py-2 text-right font-semibold tabular-nums ${
                  totals.balanced ? 'text-success' : 'text-warning'
                }`}>
                  {totals.balanced ? '✓ Balanced' : `₹ ${inr(Math.abs(totals.diff))} ${
                    totals.diff > 0 ? '(excess debit)' : '(excess credit)'
                  }`}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function rid(): string {
  return Math.random().toString(36).slice(2, 9);
}
